import { useState, useRef, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import {
  ArrowLeft,
  Bot,
  User as UserIcon,
  Loader2,
  Send,
  CheckCircle2,
  XCircle,
  ExternalLink,
  WifiOff,
  AlertTriangle,
  Paperclip,
  X,
  Check,
  AlertCircle,
  ImagePlus,
  FileText,
  Trash2,
  GitPullRequest,
  Ban,
} from "lucide-react";
import { usePageTitle } from "@/hooks/use-page-title";
import {
  useJob,
  usePostTurn,
  useMachines,
  useUploadAttachment,
  useCancelJob,
  useJobStream,
} from "@/hooks/queries/use-mailbox";
import { DraftReplyBubble } from "@/components/mailbox/draft-reply-bubble";
import type { ConnectionStatus } from "@/hooks/useEventSource";
import {
  useInterviews,
  useArchiveInterview,
} from "@/hooks/queries/use-interviews";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { ExecutionProgressPanel } from "@/components/epic-requests/execution-progress-panel";
import { ContinuationLinks, WaitingNotice, isInContinuationChain } from "@/components/mailbox/job-chain";
import { InterviewFlowPanel } from "@/components/epic-requests/interview-flow-panel";
import { useKeyInterviewDriver } from "@/hooks/queries/use-key-interview-driver";
import { useToast } from "@/hooks/useToast";
import { cn } from "@/lib/utils";
import { ApiError } from "@/lib/api/client";
import {
  ATTACHMENT_ACCEPT,
  isImageMimeType,
  machineReadiness,
  validateAttachment,
} from "@/lib/api/mailbox";
import { OutdatedMachineNotice } from "@/components/machines/outdated-machine-notice";
import { PullRequestReviewLink } from "@/components/pull-requests/pull-request-review-link";
import type { JobStatus, JobAttachment } from "@/lib/api/mailbox";
import { TERMINAL_JOB_STATUSES, looksLikeSecret } from "@mycompbox/shared";
import { NeedsInputCard } from "@/components/mailbox/needs-input-card";
import { MergeLog } from "@/components/mailbox/merge-log";
import { RunTimings, extractRunTimings } from "@/components/mailbox/run-timings";
import { WaitingPullRequests } from "@/components/mailbox/waiting-pull-requests";
import { useIdeaHref } from "@/hooks/use-new-look";

/** Job states after which a stale machine no longer matters (nothing left to wait on). */
const JOB_TERMINAL_STATUSES = new Set<JobStatus>(TERMINAL_JOB_STATUSES);

/**
 * Whether the person can still cancel a job (ENG-302): anything not finished
 * and not already being cancelled.
 */
export function canCancelJob(status: JobStatus): boolean {
  return !JOB_TERMINAL_STATUSES.has(status) && status !== "cancelling";
}

/**
 * Whether a job's result says it was cancelled (the daemon and the cloud
 * workspace report `{ cancelled: true, message }`). Used to keep a cancelled
 * run from ever showing the failure banner.
 */
function isCancelledResult(result: unknown): boolean {
  return typeof result === "object" && result !== null && (result as Record<string, unknown>).cancelled === true;
}

/** The runner's note on a cancelled job (e.g. where the kept work lives), if any. */
function extractCancelMessage(result: unknown): string | null {
  if (!isCancelledResult(result)) return null;
  const value = (result as Record<string, unknown>).message;
  return typeof value === "string" && value.length > 0 ? value : null;
}

/**
 * Mailbox job landing page (ENG-123): the turn-based interview UI.
 *
 * Renders the request-formulator's turns as a chat log and lets the user
 * reply while the job is `awaiting_input` (post-turn). Polling (useJob) keeps
 * the log live without any streaming/PTY. Terminal states (done/failed) show
 * a clear completion banner instead of a silent stall.
 *
 * `result` contract (produced by the ENG-122 daemon, not yet built): for an
 * `epic_request` job that completes successfully, `result` is expected to be
 * `{ epicRequestId: string }` so this page can link to the submitted request.
 * Rendering below is defensive — an unrecognized/absent result shape still
 * shows a generic success/failure banner rather than breaking.
 */

const statusLabels: Record<JobStatus, string> = {
  pending: "Waiting for your machine",
  claimed: "Claimed by your machine",
  running: "Running",
  awaiting_input: "Waiting for your reply",
  waiting: "Waiting",
  cancelling: "Cancelling",
  done: "Done",
  failed: "Failed",
  cancelled: "Cancelled",
};

// Light-theme colours first, dark-theme ones under `dark:` — the dark-only
// pale text was nearly invisible on the light page.
const statusColors: Record<JobStatus, string> = {
  pending: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
  claimed: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
  running: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
  awaiting_input: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300",
  waiting: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
  cancelling: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300",
  done: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
  failed: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
  cancelled: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
};

/**
 * Whether the agent owes the next turn and is (expected to be) actively working:
 * the job is claimed/running and either there are no turns yet or the last turn
 * is the user's. Drives the "agent is thinking" indicator (bug #3) so a slow
 * model call doesn't read as a frozen "Waiting for the agent..." stall.
 */
export function isAgentWorking(
  status: JobStatus,
  lastTurnRole: "agent" | "user" | undefined,
): boolean {
  return (
    (status === "claimed" || status === "running") &&
    (lastTurnRole === undefined || lastTurnRole === "user")
  );
}

/** The page heading for a job: interviews, planning, builds and plan improvements read differently. */
export function jobHeading(job: { type: string; payload?: unknown }): string {
  if (job.type === "epic_request") return "Request-Formulator Interview";
  if (job.type === "start_execution") {
    let payload: unknown = job.payload;
    if (typeof payload === "string") {
      try {
        payload = JSON.parse(payload);
      } catch {
        payload = null;
      }
    }
    const kind = payload && typeof payload === "object" ? (payload as { kind?: unknown }).kind : undefined;
    if (kind === "plan") return "Planning";
    if (kind === "orchestrate" || kind === "follow_up") return "Build";
    if (kind === "refine") return "Improve plan";
  }
  return "Run";
}

/** The finished banner's title: an interview, a plan, a plan improvement or a build. */
export function jobDoneTitle(job: { type: string; payload?: unknown }): string {
  switch (jobHeading(job)) {
    case "Request-Formulator Interview":
      return "Interview complete";
    case "Planning":
      return "Plan ready";
    case "Improve plan":
      return "Plan improved";
    case "Build":
      return "Build complete";
    default:
      return "Run complete";
  }
}

/** Best-effort extraction of an epicRequestId from a job's result, if present. */
function extractEpicRequestId(result: unknown): string | null {
  if (typeof result !== "object" || result === null) return null;
  const value = (result as Record<string, unknown>).epicRequestId;
  return typeof value === "string" && value.length > 0 ? value : null;
}

/**
 * Where an execution run's code ended up: the pull request (or a link to open
 * one) and its branch, as reported by the Agent Runner.
 */
export function extractCodeChanges(result: unknown): { url: string; isPullRequest: boolean; branch: string | null } | null {
  if (typeof result !== "object" || result === null) return null;
  const r = result as Record<string, unknown>;
  const branch = typeof r.branch === "string" ? r.branch : null;
  if (typeof r.prUrl === "string" && r.prUrl.startsWith("https://")) return { url: r.prUrl, isPullRequest: true, branch };
  if (typeof r.compareUrl === "string" && r.compareUrl.startsWith("https://")) {
    return { url: r.compareUrl, isPullRequest: false, branch };
  }
  return null;
}

/** Best-effort extraction of an error message from a failed job's result. */
function extractErrorMessage(result: unknown): string | null {
  if (typeof result !== "object" || result === null) return null;
  const value = (result as Record<string, unknown>).error;
  return typeof value === "string" && value.length > 0 ? value : null;
}

/**
 * A staged attachment in the reply tray (ENG-152). Tracks its own upload
 * lifecycle so several concurrent uploads each show their own spinner → check
 * (or inline error) independently.
 *  - `previewUrl`   — a `blob:` object URL for picked/pasted files, or the
 *                     signed/source URL for URL-ingested images.
 *  - `isObjectUrl`  — whether `previewUrl` must be `URL.revokeObjectURL`'d on
 *                     removal to avoid leaking the blob.
 *  - `attachmentId` — the server id, present once `status === "done"`; this is
 *                     what gets linked to the sent turn.
 *  - `isImage`      — whether to render an image thumbnail; documents (PDF,
 *                     text, CSV, Markdown, JSON — ENG-372) render as a file
 *                     chip instead, since they have no image preview.
 */
export interface TrayItem {
  key: string;
  status: "uploading" | "done" | "error";
  previewUrl: string;
  isObjectUrl: boolean;
  name: string;
  isImage: boolean;
  attachmentId?: string;
  error?: string;
}

/** Short human label for a document attachment's type, e.g. "PDF". */
export function documentLabel(mimeType: string): string {
  switch ((mimeType.split(";")[0] ?? "").trim().toLowerCase()) {
    case "application/pdf":
      return "PDF";
    case "text/csv":
      return "CSV";
    case "text/markdown":
      return "Markdown";
    case "application/json":
      return "JSON";
    case "text/plain":
      return "Text";
    default:
      return "File";
  }
}

/** Collect the server ids of tray items that finished uploading successfully. */
export function collectUploadedIds(tray: TrayItem[]): string[] {
  const ids: string[] = [];
  for (const item of tray) {
    // The truthy check narrows attachmentId from `string | undefined` to
    // `string`, so no assertion is needed to push it.
    if (item.status === "done" && item.attachmentId) {
      ids.push(item.attachmentId);
    }
  }
  return ids;
}

/**
 * Attachments linked to an existing turn (ENG-151 read-contract). Images render
 * as thumbnails; documents (ENG-372) render as a file chip. Each links out to
 * the full signed URL. Renders nothing when a turn has no attachments (the
 * common case), so it's safe to drop into every bubble unconditionally.
 */
export function TurnAttachments({
  attachments,
  align,
}: {
  attachments: JobAttachment[] | undefined;
  align: "user" | "agent";
}) {
  if (!attachments || attachments.length === 0) return null;
  return (
    <div
      className={cn(
        "mt-2 flex flex-wrap gap-2",
        align === "user" && "justify-end"
      )}
    >
      {attachments.map((attachment) =>
        isImageMimeType(attachment.mimeType) ? (
          <a
            key={attachment.id}
            href={attachment.signedUrl}
            target="_blank"
            rel="noreferrer"
            className="block rounded-md overflow-hidden border border-white/10 hover:border-white/25 transition-colors"
          >
            <img
              src={attachment.signedUrl}
              alt="Attached image"
              loading="lazy"
              className="h-24 w-24 object-cover"
            />
          </a>
        ) : (
          <a
            key={attachment.id}
            href={attachment.signedUrl}
            target="_blank"
            rel="noreferrer"
            aria-label={`Attached file (${documentLabel(attachment.mimeType)})`}
            className="flex h-24 w-24 flex-col items-center justify-center gap-1 rounded-md border border-white/10 bg-white/[0.03] text-xs text-muted-foreground hover:border-white/25 transition-colors"
          >
            <FileText className="h-6 w-6" />
            <span>{documentLabel(attachment.mimeType)}</span>
          </a>
        )
      )}
    </div>
  );
}

/** Label for a needs-input build the owner has answered (ENG-340). */
const REPLY_RECEIVED_LABEL = "Answer received — resuming";

/**
 * A build that's running on a computer and takes notes from its owner while
 * it works (an epic build, not a fix run on a pull request).
 */
export function isRunningBuild(job: { type: string; status: JobStatus; runsOn: string; payload: unknown }): boolean {
  const payload = typeof job.payload === "object" && job.payload !== null ? (job.payload as Record<string, unknown>) : {};
  return (
    job.type === "start_execution" &&
    job.runsOn === "computer" &&
    (job.status === "running" || job.status === "claimed") &&
    payload.kind === "orchestrate" &&
    (payload.fix === undefined || payload.fix === null)
  );
}

/** ER-130: a build whose computer hasn't called in for this long isn't running anything. */
export const RUNNER_OFFLINE_AFTER_MS = 3 * 60 * 1000;

/**
 * ER-130: one honest state for a build, where the plain job status would
 * mislead: waiting for a deploy, stopped (with the reason), or "running" on
 * a computer that has stopped calling in. Null → the normal status label.
 */
export function buildRunState(
  job: { type: string; status: JobStatus; waitingReason?: string | null; result: unknown; machineLastSeenAt?: string | null; runsOn: string },
  now: number
): { label: string; detail: string | null } | null {
  if (job.type !== "start_execution") return null;
  if (job.status === "waiting" && job.waitingReason === "deploy") {
    return {
      label: "Waiting for deploy",
      detail: "Your merges started a deploy. The build continues by itself once it finishes, and stops if the deploy fails.",
    };
  }
  const result = typeof job.result === "object" && job.result !== null ? (job.result as Record<string, unknown>) : {};
  if (job.status === "failed" && (result.stopReason === "orphaned" || result.stopReason === "deploy_failed")) {
    return { label: "Stopped", detail: typeof result.message === "string" ? result.message : null };
  }
  const active = job.status === "claimed" || job.status === "running" || job.status === "awaiting_input";
  const lastSeen = job.machineLastSeenAt ? Date.parse(job.machineLastSeenAt) : NaN;
  if (active && job.runsOn === "computer" && Number.isFinite(lastSeen) && now - lastSeen > RUNNER_OFFLINE_AFTER_MS) {
    return {
      label: "Stopped — computer offline",
      detail:
        "Your computer hasn't checked in for a few minutes, so nothing is running on this build. Start CompBox on it again, or cancel the build and restart it.",
    };
  }
  return null;
}

function epicIdOfPayload(payload: unknown): string | null {
  if (typeof payload !== "object" || payload === null) return null;
  const epicId = (payload as Record<string, unknown>).epicId;
  return typeof epicId === "string" ? epicId : null;
}

export function MailboxJobDetailPage() {
  const ideaHref = useIdeaHref();
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  // ENG-389: while the job's event stream is connected, updates arrive live and
  // the job poll slows to a safety net. The stream depends on the job, so its
  // status feeds back into the poll on the next render.
  const [streamStatus, setStreamStatus] = useState<ConnectionStatus | undefined>(undefined);
  const { data: job, isLoading } = useJob(jobId, { streamStatus });
  // ENG-389: the agent's reply as it streams in, before the turn is stored.
  const { draft, status: jobStreamStatus } = useJobStream(jobId, {
    enabled: !!job && !JOB_TERMINAL_STATUSES.has(job.status),
  });
  useEffect(() => {
    setStreamStatus(jobStreamStatus);
  }, [jobStreamStatus]);
  const postTurn = usePostTurn(jobId ?? "");
  const cancelJob = useCancelJob();
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  // Machine online/offline visibility — without this, a daemon that dies
  // mid-job leaves the user staring at an infinite "Running..." spinner with
  // no explanation (a silent stall). useMachines already polls every 15s.
  const { data: machines } = useMachines();
  // Interviews on an API key have no daemon: this page asks for each step.
  const keyStepError = useKeyInterviewDriver(job);

  // Discard affordance (stuck-interview escape hatch): when THIS failed job is
  // still the current job of an in-progress interview, that interview is
  // zombie state — it will keep triggering resume detection even though its
  // driving job is dead. Offer an explicit archive ("discard") so the user can
  // start fresh. Lookup mirrors InterviewFlowPanel's job→interview correlation.
  const isFailedEpicRequestJob =
    job?.type === "epic_request" && job.status === "failed";
  const { data: inProgressInterviews } = useInterviews(
    { status: "in_progress" },
    { enabled: isFailedEpicRequestJob }
  );
  const stuckInterviews = isFailedEpicRequestJob
    ? (inProgressInterviews?.data.filter((i) => i.jobId === job.id) ?? [])
    : [];
  const stuckInterview = stuckInterviews[0] ?? null;
  const archiveInterview = useArchiveInterview();
  const handleDiscardInterview = async () => {
    if (stuckInterviews.length === 0) return;
    try {
      // Archive EVERY in-progress interview pointing at this job, not just the
      // first: duplicates created before interview-dedup landed re-surface one
      // at a time after each single archive, making discard look like a no-op.
      for (const interview of stuckInterviews) {
        await archiveInterview.mutateAsync(interview.id);
      }
      toast("Interview discarded — you can start a new one.");
    } catch (error) {
      toast(
        error instanceof ApiError
          ? error.message
          : "Failed to discard the interview"
      );
    }
  };

  const [reply, setReply] = useState("");
  // Needs-input replies (ENG-340): a reply that looks like a secret needs an
  // explicit "send anyway"; the API may still refuse it (422).
  const [sendSecretAnyway, setSendSecretAnyway] = useState(false);
  const [replyError, setReplyError] = useState<string | null>(null);
  const turnsEndRef = useRef<HTMLDivElement>(null);

  // Attachment tray (ENG-152): staged images + their upload lifecycle. The
  // upload hook is just the transport; per-item spinner/check/error state lives
  // here so multiple concurrent uploads each track their own status.
  const uploadAttachment = useUploadAttachment(jobId ?? "");
  const [tray, setTray] = useState<TrayItem[]>([]);
  const [attachError, setAttachError] = useState<string | null>(null);
  const [urlInput, setUrlInput] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const keyCounter = useRef(0);
  // Mirror the tray in a ref so the unmount cleanup can revoke every object URL
  // without re-subscribing the effect on each tray change.
  const trayRef = useRef<TrayItem[]>([]);
  trayRef.current = tray;
  useEffect(
    () => () => {
      for (const item of trayRef.current) {
        if (item.isObjectUrl) URL.revokeObjectURL(item.previewUrl);
      }
    },
    []
  );

  // A build parked on a question for its owner (ENG-338) reads like an
  // interview waiting on a reply; once answered it says it is resuming.
  const needsInputWait = job?.status === "waiting" && job.waitingReason === "needs_input";
  const replyReceived = job?.status === "waiting" && job.waitingReason === "reply_received";
  const displayStatus: JobStatus | undefined = needsInputWait ? "awaiting_input" : job?.status;

  usePageTitle(
    job ? (replyReceived ? REPLY_RECEIVED_LABEL : statusLabels[displayStatus ?? job.status]) : "Job"
  );

  // Auto-scroll to the newest turn as the interview progresses.
  useEffect(() => {
    turnsEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [job?.turns.length]);

  const handleBack = () => {
    void navigate("/epic-requests");
  };

  // A request conversation on the person's API key reopens when they write
  // again, even after it finished.
  const reopenable =
    !!job &&
    job.type === "epic_request" &&
    job.runsOn === "cloud" &&
    JOB_TERMINAL_STATUSES.has(job.status) &&
    // The server never reopens a cancelled job (ENG-299).
    job.status !== "cancelled";
  // Only the owner can open a job page at all (GET /jobs/:id), so a needs-input
  // wait is always answerable here; the API re-checks ownership on reply.
  // A note to a running build: its computer stops the agent's current step
  // and hands the note over. Fix runs carry their instructions instead.
  const buildNote = !!job && isRunningBuild(job);
  const canReply = job?.status === "awaiting_input" || reopenable || needsInputWait || buildNote;
  // Images aren't passed to a running build, only text.
  const canAttach = canReply && !buildNote;
  const secretCheck = (needsInputWait || buildNote) && reply.trim() ? looksLikeSecret(reply) : { secret: false };
  const blockedBySecret = secretCheck.secret && !sendSecretAnyway;

  const uploadedIds = collectUploadedIds(tray);
  const hasUploading = tray.some((item) => item.status === "uploading");

  /** Remove a staged item, revoking its object URL if we own one. */
  const removeTrayItem = (key: string) => {
    setTray((prev) => {
      const item = prev.find((i) => i.key === key);
      if (item?.isObjectUrl) URL.revokeObjectURL(item.previewUrl);
      return prev.filter((i) => i.key !== key);
    });
  };

  /**
   * Client-validate then upload each file, staging a tray item that flips from
   * spinner → check (or → inline error). Validation here is UX only; the API is
   * authoritative and re-checks size/MIME.
   */
  const stageFiles = (files: File[]) => {
    if (!canAttach) return;
    for (const file of files) {
      const validationError = validateAttachment(file);
      if (validationError) {
        setAttachError(`${file.name}: ${validationError}`);
        continue;
      }
      setAttachError(null);
      const key = `att-${String(keyCounter.current++)}`;
      // Only images get an object-URL preview; documents show a file chip.
      const isImage = file.type.startsWith("image/");
      const previewUrl = isImage ? URL.createObjectURL(file) : "";
      setTray((prev) => [
        ...prev,
        {
          key,
          status: "uploading",
          previewUrl,
          isObjectUrl: isImage,
          name: file.name || (isImage ? "image" : "file"),
          isImage,
        },
      ]);
      void uploadAttachment
        .mutateAsync({ file })
        .then((attachment) => {
          setTray((prev) =>
            prev.map((i) =>
              i.key === key
                ? { ...i, status: "done", attachmentId: attachment.id }
                : i
            )
          );
        })
        .catch((error: unknown) => {
          setTray((prev) =>
            prev.map((i) =>
              i.key === key
                ? {
                    ...i,
                    status: "error",
                    error:
                      error instanceof ApiError
                        ? error.message
                        : "Upload failed",
                  }
                : i
            )
          );
        });
    }
  };

  /** Ingest an image by URL (the JSON `{ url }` upload variant). */
  const stageUrl = () => {
    const url = urlInput.trim();
    if (!url || !canAttach) return;
    setUrlInput("");
    setAttachError(null);
    const key = `att-${String(keyCounter.current++)}`;
    setTray((prev) => [
      ...prev,
      {
        key,
        status: "uploading",
        previewUrl: url,
        isObjectUrl: false,
        name: url,
        isImage: true,
      },
    ]);
    void uploadAttachment
      .mutateAsync({ url })
      .then((attachment) => {
        setTray((prev) =>
          prev.map((i) =>
            i.key === key
              ? {
                  ...i,
                  status: "done",
                  attachmentId: attachment.id,
                  previewUrl: attachment.signedUrl,
                  isImage: isImageMimeType(attachment.mimeType),
                }
              : i
          )
        );
      })
      .catch((error: unknown) => {
        setTray((prev) =>
          prev.map((i) =>
            i.key === key
              ? {
                  ...i,
                  status: "error",
                  error:
                    error instanceof ApiError ? error.message : "Upload failed",
                }
              : i
          )
        );
      });
  };

  const handleBrowseChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length > 0) stageFiles(files);
    // Reset so re-picking the same file still fires onChange.
    e.target.value = "";
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const files: File[] = [];
    for (const item of Array.from(e.clipboardData.items)) {
      // Any pasted file is staged; stageFiles validates type/size (ENG-372).
      if (item.kind === "file") {
        const file = item.getAsFile();
        if (file) files.push(file);
      }
    }
    if (files.length > 0) {
      // Keep the pasted file out of the text box; stage it instead.
      e.preventDefault();
      stageFiles(files);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
    if (!canAttach) return;
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) stageFiles(files);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (canAttach) setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleSend = async () => {
    const content = reply.trim();
    const attachmentIds = collectUploadedIds(tray);
    // Allow sending with only attachments (no text), but never while an upload
    // is still in flight — that would drop the not-yet-linked image.
    if ((!content && attachmentIds.length === 0) || !canReply || hasUploading || blockedBySecret) {
      return;
    }
    setReplyError(null);
    try {
      setReply("");
      setSendSecretAnyway(false);
      await postTurn.mutateAsync({
        content,
        ...(attachmentIds.length > 0 ? { attachmentIds } : {}),
      });
      if (buildNote) toast("Note sent. The build reads it within a minute.");
      // Success: clear the tray (and free object URLs). We only clear on success
      // so a failed send keeps the staged images for a retry.
      setTray((prev) => {
        for (const item of prev) {
          if (item.isObjectUrl) URL.revokeObjectURL(item.previewUrl);
        }
        return [];
      });
    } catch (error) {
      // Restore the draft so the user doesn't lose their answer.
      setReply(content);
      const message =
        error instanceof ApiError ? error.message : "Failed to send your reply";
      if (needsInputWait || buildNote) setReplyError(message);
      toast(message);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 space-y-4">
        <div className="h-8 w-48 animate-pulse rounded bg-muted" />
        <div className="h-24 w-full animate-pulse rounded bg-muted" />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="pt-4">
        <p className="text-muted-foreground">Job not found</p>
        <Button variant="ghost" onClick={handleBack} className="mt-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to epic requests
        </Button>
      </div>
    );
  }

  const epicRequestId = job.status === "done" ? extractEpicRequestId(job.result) : null;
  // A request conversation that has saved its request but is still open.
  const savedRequestId =
    job.type === "epic_request" && !JOB_TERMINAL_STATUSES.has(job.status) ? extractEpicRequestId(job.result) : null;
  // A cancelled run is never shown as a failure, even if a runner reported it
  // as failed (it carries `cancelled: true` in its result).
  const showFailure = job.status === "failed" && !isCancelledResult(job.result);
  const errorMessage = showFailure ? extractErrorMessage(job.result) : null;
  const cancelMessage = job.status === "cancelled" ? extractCancelMessage(job.result) : null;
  const codeChanges = JOB_TERMINAL_STATUSES.has(job.status) ? extractCodeChanges(job.result) : null;
  // ENG-352: where the run's time went (only runners that report it).
  const runTimings = extractRunTimings(job.result);

  // "Agent is thinking" affordance (bug #3): while the daemon owes the next turn
  // (job claimed/running and the last turn is the user's, or there are none yet),
  // show an active indicator so a slow model call doesn't look like a frozen
  // "Waiting for the agent..." stall. A dead daemon is covered separately by the
  // machine-not-ready warning; a converged/failed job by the terminal banners.
  const lastTurn = job.turns[job.turns.length - 1];
  const agentIsWorking = isAgentWorking(job.status, lastTurn?.role);

  // Cancelling any job in an auto-continuation chain cancels the chain (ENG-334).
  const inChain = isInContinuationChain(job);

  // A not-ready machine only matters while the job still has work left to do —
  // once done/failed, the daemon's connectivity is no longer relevant. We use
  // the server-derived readiness so we can tell "daemon down" (offline) apart
  // from "daemon up but agent runtime unavailable" (runtime-not-found).
  const onKey = job.runsOn === "cloud";
  const machine = onKey ? undefined : machines?.find((m) => m.id === job.machineId);
  const machineReady = machine ? machineReadiness(machine, Date.now(), job.type) : null;
  const notReadyMachine =
    !JOB_TERMINAL_STATUSES.has(job.status) && machine && machineReady !== "ready"
      ? machine
      : null;

  // ER-130: the honest state of a build, and its epic's pull requests.
  const runState = buildRunState(job, Date.now());
  const buildEpicId = epicIdOfPayload(job.payload);

  const handleConfirmCancel = () => {
    cancelJob.mutate(job.id, {
      onError: (error) => {
        // e.g. 403 when someone other than the owner or an org admin tries.
        const message = error instanceof ApiError ? error.message : "Failed to cancel the job";
        toast(message);
      },
    });
    setConfirmCancelOpen(false);
  };

  return (
    <div className="pt-4">
      <div className="flex items-center gap-3 mb-4">
        <Button variant="ghost" size="sm" onClick={handleBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
      </div>

      <div className="glass-card rounded-2xl mx-0 mb-4">
        <div className="px-4 md:px-6 py-4 flex items-center justify-between gap-3">
          <div>
            <h1 className="text-lg font-semibold">{jobHeading(job)}</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {onKey ? "Running on CompBox with your API key" : "Running on your paired machine"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {canCancelJob(job.status) && (
              <Button
                variant="outline"
                size="sm"
                disabled={cancelJob.isPending}
                onClick={() => {
                  setConfirmCancelOpen(true);
                }}
              >
                <Ban className="h-4 w-4 mr-1.5" />
                {inChain ? "Cancel build chain" : "Cancel"}
              </Button>
            )}
            <Badge className={cn("text-xs font-medium capitalize", statusColors[displayStatus ?? job.status])}>
              {!runState &&
                (job.status === "pending" ||
                  job.status === "claimed" ||
                  job.status === "running" ||
                  job.status === "cancelling") && <Loader2 className="h-3 w-3 mr-1.5 animate-spin inline" />}
              {runState
                ? runState.label
                : onKey && (job.status === "claimed" || job.status === "running")
                  ? "Thinking"
                  : replyReceived
                    ? REPLY_RECEIVED_LABEL
                    : statusLabels[displayStatus ?? job.status]}
            </Badge>
          </div>
        </div>

        {needsInputWait && (
          <div className="border-t border-purple-800/30 px-4 md:px-6 py-4 bg-purple-900/[0.06]">
            {job.needsInput ? (
              <NeedsInputCard request={job.needsInput} />
            ) : (
              <p className="text-sm">The build needs an answer from you. Reply below to continue.</p>
            )}
          </div>
        )}
        {replyReceived && (
          <div
            className="border-t border-purple-800/30 px-4 md:px-6 py-4 bg-purple-900/[0.06] text-sm"
            data-testid="reply-received"
          >
            {REPLY_RECEIVED_LABEL}. Your machine picks the build up again where it stopped.
          </div>
        )}
        {runState?.detail && (
          <div
            className="border-t border-amber-800/30 px-4 md:px-6 py-4 bg-amber-900/[0.06] text-sm text-amber-800 dark:text-amber-300"
            data-testid="run-state"
          >
            {runState.detail}
          </div>
        )}
        {job.type === "start_execution" && buildEpicId && <WaitingPullRequests epicId={buildEpicId} job={job} />}
        {job.status === "waiting" && !needsInputWait && !replyReceived && !runState && (
          <div className="border-t border-amber-800/30 px-4 md:px-6 py-4 bg-amber-900/[0.06] text-sm text-amber-800 dark:text-amber-300">
            <WaitingNotice resumeAt={job.resumeAt} />
          </div>
        )}
        {inChain && (
          <div className="border-t px-4 md:px-6 py-3">
            <ContinuationLinks job={job} />
          </div>
        )}
        {runTimings && (
          <div className="border-t px-4 md:px-6 py-3">
            <RunTimings timings={runTimings} />
          </div>
        )}
        {job.mergeRecords && job.mergeRecords.length > 0 && (
          <div className="border-t px-4 md:px-6 py-3">
            <MergeLog records={job.mergeRecords} />
          </div>
        )}

        {keyStepError && !JOB_TERMINAL_STATUSES.has(job.status) && (
          <div className="border-t border-amber-800/30 px-4 md:px-6 py-4 bg-amber-900/[0.06]">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-400" />
              <span className="text-sm text-amber-300">{keyStepError} Retrying automatically.</span>
            </div>
          </div>
        )}

        {/* Machine-not-ready warning — explains a stalled non-terminal job
            instead of leaving the user staring at a silent spinner. Distinct
            copy for "daemon down" vs "daemon up but agent runtime missing". */}
        {notReadyMachine && machineReady === "runtime-not-found" && (
          <div className="border-t border-amber-800/30 px-4 md:px-6 py-4 bg-amber-900/[0.06]">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="h-4 w-4 text-amber-400" />
              <span className="text-sm font-semibold text-amber-300">
                Agent runtime not found on {notReadyMachine.name}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              This machine is connected but the Claude Code agent runtime
              wasn&apos;t found. Install Claude Code (
              <a
                href="https://code.claude.com"
                target="_blank"
                rel="noreferrer"
                className="underline decoration-dotted underline-offset-2 hover:text-foreground"
              >
                code.claude.com
              </a>
              ), or if it&apos;s already installed, update the CompBox agent (
              <code className="bg-muted px-1 py-0.5 rounded text-xs">
                compbox run
              </code>
              ) so it can report status. This job will resume automatically once
              the runtime is available — nothing is lost.
            </p>
          </div>
        )}
        {/* ENG-322: the server only refuses to hand a *pending* job to an
            outdated CLI; one already claimed keeps running, so stay quiet then. */}
        {notReadyMachine && machineReady === "outdated" && job.status === "pending" && (
          <OutdatedMachineNotice
            machine={notReadyMachine}
            jobKind={job.type}
            className="border-t border-amber-800/30 px-4 md:px-6 py-4 bg-amber-900/[0.06]"
          />
        )}
        {notReadyMachine && machineReady === "offline" && (
          <div className="border-t border-amber-800/30 px-4 md:px-6 py-4 bg-amber-900/[0.06]">
            <div className="flex items-center gap-2 mb-1">
              <WifiOff className="h-4 w-4 text-amber-400" />
              <span className="text-sm font-semibold text-amber-300">
                Your CompBox session isn&apos;t running
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              <strong>{notReadyMachine.name}</strong> hasn&apos;t checked in recently.
              This job will resume automatically once the daemon (
              <code className="bg-muted px-1 py-0.5 rounded text-xs">
                compbox run
              </code>
              ) is back online — nothing is lost.
            </p>
          </div>
        )}

        {/* Where the run's code is: its pull request (or a link to open one). */}
        {codeChanges && (
          <div className="border-t border-white/[0.06] px-4 md:px-6 py-3 flex flex-wrap items-center gap-2 text-sm">
            <GitPullRequest className="h-4 w-4 text-primary" />
            <a
              href={codeChanges.url}
              target="_blank"
              rel="noreferrer"
              className="font-medium text-primary underline underline-offset-2"
            >
              {codeChanges.isPullRequest ? "View the pull request" : "Open a pull request for this work"}
            </a>
            {codeChanges.branch && (
              <span className="text-xs text-muted-foreground">
                on branch <code className="bg-muted px-1 py-0.5 rounded">{codeChanges.branch}</code>
              </span>
            )}
            {codeChanges.isPullRequest && jobId && (
              <PullRequestReviewLink jobId={jobId} className="ml-auto text-sm font-medium text-primary underline underline-offset-2" />
            )}
          </div>
        )}

        {/* The request this conversation saved; the chat stays open below. */}
        {savedRequestId && (
          <div className="border-t border-green-800/30 px-4 md:px-6 py-3 bg-green-900/[0.06] flex flex-wrap items-center gap-x-3 gap-y-2">
            <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
            <span className="text-sm font-semibold text-green-700 dark:text-green-300">Request saved</span>
            <Link
              to={ideaHref(savedRequestId)}
              className="inline-flex items-center gap-1.5 text-sm text-primary underline underline-offset-2"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              View it
            </Link>
            <span className="text-xs text-muted-foreground">
              Keep talking here any time to add detail or raise its score.
            </span>
          </div>
        )}

        {/* Terminal-state banners */}
        {job.status === "done" && (
          <div className="border-t border-green-800/30 px-4 md:px-6 py-4 bg-green-900/[0.06]">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
              <span className="text-sm font-semibold text-green-700 dark:text-green-300">
                {jobDoneTitle(job)}
              </span>
            </div>
            {epicRequestId ? (
              <>
                <p className="text-xs text-muted-foreground mb-2">
                  Your epic request was submitted.
                  {reopenable && " You can keep talking below to add detail or raise its score."}
                </p>
                <Link
                  to={ideaHref(epicRequestId)}
                  className="inline-flex items-center gap-2 rounded-lg border border-green-800/30 bg-white/[0.02] px-3 py-2 text-sm hover:bg-white/[0.06] transition-colors"
                >
                  <ExternalLink className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
                  View your epic request
                </Link>
              </>
            ) : (
              <p className="text-xs text-muted-foreground">
                The job finished successfully.
              </p>
            )}
          </div>
        )}
        {job.status === "cancelling" && (
          <div className="border-t border-orange-800/30 px-4 md:px-6 py-4 bg-orange-900/[0.06]">
            <div className="flex items-center gap-2 mb-1">
              <Loader2 className="h-4 w-4 text-orange-600 dark:text-orange-400 animate-spin" />
              <span className="text-sm font-semibold text-orange-700 dark:text-orange-300">Cancelling</span>
            </div>
            <p className="text-xs text-muted-foreground">
              {onKey
                ? "Stopping the run on CompBox. This page updates when it has stopped."
                : "Waiting for your machine to stop the run. This page updates when it has stopped."}
            </p>
          </div>
        )}
        {job.status === "cancelled" && (
          <div className="border-t border-gray-700/40 px-4 md:px-6 py-4 bg-gray-800/[0.2]">
            <div className="flex items-center gap-2 mb-1">
              <Ban className="h-4 w-4 text-gray-600 dark:text-gray-400" />
              <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Cancelled</span>
            </div>
            <p className="text-xs text-muted-foreground">
              {cancelMessage ?? "This run was cancelled. Nothing was pushed."}
            </p>
          </div>
        )}
        {showFailure && (
          <div className="border-t border-red-800/30 px-4 md:px-6 py-4 bg-red-900/[0.06]">
            <div className="flex items-center gap-2 mb-1">
              <XCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
              <span className="text-sm font-semibold text-red-700 dark:text-red-300">
                {job.type === "epic_request" ? "The interview failed" : "The run stopped"}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              {errorMessage ??
                (onKey
                  ? "Something went wrong running the interview. Try starting it again."
                  : "Something went wrong on your machine. Check the daemon logs and try again.")}
            </p>
            {stuckInterview && (
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                disabled={archiveInterview.isPending}
                onClick={() => void handleDiscardInterview()}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                {archiveInterview.isPending
                  ? "Discarding..."
                  : "Discard interview"}
              </Button>
            )}
            {machineReady === "runtime-not-found" && (
              <p className="mt-2 text-xs text-muted-foreground">
                This machine reports its Claude Code agent runtime is
                unavailable. Install Claude Code (
                <a
                  href="https://code.claude.com"
                  target="_blank"
                  rel="noreferrer"
                  className="underline decoration-dotted underline-offset-2 hover:text-foreground"
                >
                  code.claude.com
                </a>
                ), or if it&apos;s already installed, update the CompBox agent so
                it can report status — then try again.
              </p>
            )}
          </div>
        )}

        {/* Execution progress — appears once the job is linked to a running epic */}
        {job.progress && <ExecutionProgressPanel progress={job.progress} />}
      </div>

      {/* Deeper-interview panel (ENG-144): the section roadmap plus the
          reopen/enrich affordance once the interview is complete. Only renders
          for epic_request jobs (execution jobs reuse this page too). */}
      <InterviewFlowPanel job={job} />

      <div className="glass-card rounded-2xl">
        <div className="max-w-3xl mx-auto p-6 space-y-4">
          {job.turns.length === 0 && !agentIsWorking ? (
            <div className="text-center py-10 text-muted-foreground text-sm">
              {job.status === "pending"
                ? "Waiting for your machine to pick up this job..."
                : "Waiting for the interview to begin..."}
            </div>
          ) : (
            <>
              {job.turns.map((turn) => (
                <div
                  key={turn.id}
                  className={cn(
                    "flex gap-3",
                    turn.role === "user" && "flex-row-reverse"
                  )}
                >
                  <div
                    className={cn(
                      "h-8 w-8 shrink-0 rounded-full flex items-center justify-center",
                      turn.role === "agent"
                        ? "bg-purple-900/30 text-purple-300"
                        : "bg-blue-900/30 text-blue-300"
                    )}
                  >
                    {turn.role === "agent" ? (
                      <Bot className="h-4 w-4" />
                    ) : (
                      <UserIcon className="h-4 w-4" />
                    )}
                  </div>
                  <div
                    className={cn(
                      "rounded-lg px-4 py-2.5 text-sm max-w-[75%] whitespace-pre-wrap",
                      turn.role === "agent"
                        ? "bg-white/[0.04] border border-white/[0.06]"
                        : "bg-primary text-primary-foreground"
                    )}
                  >
                    {turn.content}
                    <TurnAttachments
                      attachments={turn.attachments}
                      align={turn.role === "user" ? "user" : "agent"}
                    />
                    <div
                      className={cn(
                        "text-[10px] mt-1 opacity-60",
                        turn.role === "user" ? "text-right" : ""
                      )}
                    >
                      {formatDistanceToNow(new Date(turn.createdAt), { addSuffix: true })}
                    </div>
                  </div>
                </div>
              ))}
              {/* Only while the agent owes the turn: once the stored agent turn
                  lands, the draft is gone even before job.turn clears it. */}
              {agentIsWorking && draft && <DraftReplyBubble text={draft} />}
              {agentIsWorking && !draft && (
                <div className="flex gap-3" data-testid="agent-thinking">
                  <div className="h-8 w-8 shrink-0 rounded-full flex items-center justify-center bg-purple-900/30 text-purple-300">
                    <Bot className="h-4 w-4" />
                  </div>
                  <div className="inline-flex items-center gap-2 rounded-lg border border-white/[0.06] bg-white/[0.04] px-4 py-2.5 text-sm text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    The agent is working on your request…
                  </div>
                </div>
              )}
              <div ref={turnsEndRef} />
            </>
          )}
        </div>

        {/* Reply box — only actionable while the agent is waiting on the user.
            The whole area is a drag-and-drop target for image and document
            attachments (ENG-152, ENG-372); the tray, browse button, and
            image-URL field sit above
            the text input. */}
        {(job.status === "awaiting_input" ||
          needsInputWait ||
          job.status === "running" ||
          job.status === "claimed" ||
          job.status === "pending" ||
          reopenable) && (
          <div
            className={cn(
              "relative border-t border-white/[0.06] px-6 py-4 transition-colors",
              dragActive && "bg-primary/[0.06]"
            )}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            role="group"
            aria-label="Reply with optional attachments"
          >
            {/* Drag overlay — visible affordance while dragging files over the
                chat. Pointer-events-none so it never eats the drop. */}
            {dragActive && (
              <div
                className="pointer-events-none absolute inset-2 z-10 flex items-center justify-center rounded-xl border-2 border-dashed border-primary/50 bg-background/60 text-sm text-foreground"
                data-testid="attachment-drop-overlay"
              >
                <span className="inline-flex items-center gap-2">
                  <ImagePlus className="h-4 w-4" />
                  Drop files to attach
                </span>
              </div>
            )}

            {/* Staged-attachment tray: one thumbnail per image (or file chip
                per document) with a spinner → check acknowledgement (or an
                error state). */}
            {tray.length > 0 && (
              <ul
                className="mb-3 flex flex-wrap gap-2 list-none p-0 m-0"
                aria-label="Staged attachments"
              >
                {tray.map((item) => (
                  <li
                    key={item.key}
                    className="relative h-16 w-16 overflow-hidden rounded-md border border-white/10 bg-white/[0.03]"
                  >
                    {item.isImage ? (
                      <img
                        src={item.previewUrl}
                        alt={item.name}
                        className={cn(
                          "h-full w-full object-cover",
                          item.status !== "done" && "opacity-60"
                        )}
                      />
                    ) : (
                      <div
                        className={cn(
                          "flex h-full w-full flex-col items-center justify-center gap-0.5 px-1 text-muted-foreground",
                          item.status !== "done" && "opacity-60"
                        )}
                        data-testid="attachment-file-chip"
                        title={item.name}
                      >
                        <FileText className="h-5 w-5" />
                        <span className="w-full truncate text-center text-[10px]">
                          {item.name}
                        </span>
                      </div>
                    )}
                    <div className="absolute inset-0 flex items-center justify-center">
                      {item.status === "uploading" && (
                        <span
                          className="rounded-full bg-black/60 p-1"
                          data-testid="attachment-uploading"
                          aria-label="Uploading"
                        >
                          <Loader2 className="h-4 w-4 animate-spin text-white" />
                        </span>
                      )}
                      {item.status === "done" && (
                        <span
                          className="rounded-full bg-green-600/90 p-1"
                          data-testid="attachment-done"
                          aria-label="Attached"
                        >
                          <Check className="h-3.5 w-3.5 text-white" />
                        </span>
                      )}
                      {item.status === "error" && (
                        <span
                          className="rounded-full bg-red-600/90 p-1"
                          data-testid="attachment-error"
                          aria-label="Upload failed"
                          title={item.error}
                        >
                          <AlertCircle className="h-3.5 w-3.5 text-white" />
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => { removeTrayItem(item.key); }}
                      aria-label={`Remove ${item.name}`}
                      className="absolute right-0.5 top-0.5 rounded-full bg-black/70 p-0.5 text-white hover:bg-black"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {/* Attachment controls: browse (images open the mobile
                camera/gallery; documents the file picker) and an image-URL
                ingest field. */}
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept={ATTACHMENT_ACCEPT}
                multiple
                className="hidden"
                aria-hidden="true"
                tabIndex={-1}
                data-testid="attachment-file-input"
                onChange={handleBrowseChange}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={!canAttach}
                onClick={() => fileInputRef.current?.click()}
                aria-label="Attach files"
                className="gap-1.5"
              >
                <Paperclip className="h-4 w-4" />
                Attach
              </Button>
              <div className="flex min-w-[180px] flex-1 items-center gap-1.5">
                <input
                  type="url"
                  value={urlInput}
                  onChange={(e) => { setUrlInput(e.target.value); }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      stageUrl();
                    }
                  }}
                  disabled={!canAttach}
                  placeholder="Paste an image URL…"
                  aria-label="Image URL"
                  className="h-9 flex-1 rounded-lg border border-white/10 bg-white/[0.03] px-2.5 text-xs text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={!canAttach || !urlInput.trim()}
                  onClick={stageUrl}
                >
                  Add
                </Button>
              </div>
            </div>

            {/* Client-side validation feedback (UX only; API is authoritative). */}
            {attachError && (
              <p className="mb-2 text-xs text-red-400" role="alert">
                {attachError}
              </p>
            )}

            <div className="flex items-end gap-2">
              <Textarea
                value={reply}
                onChange={(e) => {
                  setReply(e.target.value);
                  setReplyError(null);
                }}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                placeholder={
                  buildNote
                    ? "Send the build a note, e.g. what to skip. It stops its current step and reads it. (Enter to send)"
                    : canReply
                    ? "Type your reply... (Enter to send, Shift+Enter for a new line)"
                    : agentIsWorking
                      ? "The agent is working on your request…"
                      : "Waiting for the agent to ask a question..."
                }
                disabled={!canReply || postTurn.isPending}
                className="min-h-[44px] max-h-40 resize-none"
              />
              <Button
                onClick={() => { void handleSend(); }}
                disabled={
                  !canReply ||
                  postTurn.isPending ||
                  hasUploading ||
                  blockedBySecret ||
                  (!reply.trim() && uploadedIds.length === 0)
                }
                size="icon"
                aria-label="Send reply"
                className="h-11 w-11 shrink-0"
              >
                {postTurn.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </div>
            {secretCheck.secret && (
              <div
                className="mt-2 rounded-md border border-amber-700/40 bg-amber-900/[0.08] px-3 py-2 text-xs text-amber-800 dark:text-amber-300"
                role="alert"
                data-testid="secret-warning"
              >
                <p>
                  This looks like a secret — put it in GitHub environment secrets or your cloud&apos;s
                  secret manager, not here.
                </p>
                <label className="mt-1.5 flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={sendSecretAnyway}
                    onChange={(e) => {
                      setSendSecretAnyway(e.target.checked);
                    }}
                  />
                  It is not a secret — send anyway
                </label>
              </div>
            )}
            {replyError && (
              <p className="mt-2 text-xs text-red-400" role="alert" data-testid="reply-error">
                {replyError}
              </p>
            )}
            {!canReply && (
              <p className="text-xs text-muted-foreground mt-2">
                {statusLabels[job.status]}...
              </p>
            )}
          </div>
        )}
      </div>

      <Dialog open={confirmCancelOpen} onOpenChange={setConfirmCancelOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{inChain ? "Cancel the whole build chain?" : "Cancel this run?"}</DialogTitle>
            <DialogDescription>
              {inChain && "This run and every continuation after it stop. "}
              {onKey
                ? "CompBox stops the run. Nothing is pushed and no pull request is opened."
                : "Your machine stops the run. Nothing is pushed and no pull request is opened; any work so far is kept on its branch on your machine."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setConfirmCancelOpen(false);
              }}
            >
              Keep running
            </Button>
            <Button variant="destructive" disabled={cancelJob.isPending} onClick={handleConfirmCancel}>
              {inChain ? "Cancel build chain" : "Cancel run"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
